import { useAuth } from '@/app/providers';
import { Button } from '@/components/ui';
import type { Community } from '@/types';
import { CommunityCard } from './CommunityCard';
import { DIRECTORY_GRID } from './CommunityDirectory';

interface CuratedFeedProps {
  communities: Community[];
  /** Opens the auth overlay (logged-out CTA). */
  onLogin: () => void;
}

/** "For You" curated section — ported from OG `buildForYouHTML` (_v13_features.js:153).
 *  Logged-out: the "Your curated feed awaits" CTA. Logged-in: a curated highlight
 *  (flagship first) rendered with the same CommunityCard used in the grid. */
export function CuratedFeed({ communities, onLogin }: CuratedFeedProps) {
  const { user } = useAuth();

  if (user) {
    const picks = [...communities]
      .sort((a, b) => (a.id === 'winr-official' ? -1 : b.id === 'winr-official' ? 1 : 0))
      .slice(0, 4);
    if (!picks.length) return null;
    return (
      <section className="mb-7 mt-[22px]">
        <div className="flex items-center gap-2.5">
          <h3 className="m-0 text-lg font-extrabold tracking-[-0.3px] text-white">For You</h3>
          <span className="rounded-[6px] border border-white/10 bg-white/[0.07] px-[7px] py-[3px] text-[9px] font-extrabold uppercase tracking-[0.8px] text-white/60">
            Curated
          </span>
        </div>
        <p className="mb-3.5 mt-1 text-[13px] text-lo">Picked for you based on your interests and network.</p>
        <div className={DIRECTORY_GRID}>
          {picks.map((c) => (
            <CommunityCard key={c.id} community={c} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="my-6 flex flex-col items-center gap-2 rounded-2xl border border-edge bg-white/[0.03] px-6 py-5 text-center backdrop-blur-xl">
      <div className="mb-0.5 flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-white/60">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </div>
      <h3 className="m-0 text-sm font-extrabold tracking-[-0.3px] text-white">Your curated feed awaits</h3>
      <p className="mb-1 max-w-[420px] text-xs leading-[1.45] text-mid">
        Log in to get personalized community recommendations based on your interests and network.
      </p>
      <Button
        type="button"
        size="sm"
        className="h-9 bg-white px-4 text-xs text-black shadow-[0_10px_24px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.9)] hover:-translate-y-px hover:brightness-105"
        onClick={onLogin}
      >
        Log In to Unlock
      </Button>
    </div>
  );
}
